import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, getFirestore } from "firebase/firestore";
import { useEventId } from "../EventContext";
import {
  AQUAFITX_BG,
  AQUAFITX_FONT,
  AQUAFITX_GOLD as GOLD,
  AQUAFITX_NAVY as NAVY,
  useAquaFitXFont,
} from "../utils/aquafitxTheme";

/**
 * AquaFitX Race start list, in the event's own look.
 *
 * Built for the way this event is organised: individual age categories are
 * split men / women (they are ranked that way), and doubles and relays are
 * team entries, so every member is listed under the team's BIB rather than
 * one long joined name.
 */

const shortName = (name = "") => name.replace(/^INDIVIDUAL |^TEAM /, "");

const formatDate = (iso) => {
  const date = iso ? new Date(`${iso}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date
    .toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    .toUpperCase();
};

const Bib = ({ bib }) => (
  <span
    className="shrink-0 inline-flex items-center justify-center min-w-[3.6rem] rounded-lg px-2 py-1 text-lg font-black italic tabular-nums"
    style={{ background: GOLD, color: NAVY }}
  >
    {bib}
  </span>
);

const IndividualRow = ({ p }) => (
  <li className="flex items-center gap-3 px-3 py-2.5">
    <Bib bib={p.bib} />
    <span className="min-w-0 flex-1 text-lg font-bold uppercase leading-tight break-words">{p.name}</span>
    {/* Age group in place of the age: it is what the athlete is ranked in. */}
    {(p.ageGroup || p.categoryName) && (
      <span className="shrink-0 text-sm font-semibold text-white/60">{p.ageGroup || shortName(p.categoryName)}</span>
    )}
  </li>
);

const TeamRow = ({ p }) => (
  <li className="flex items-start gap-3 px-3 py-3">
    <Bib bib={p.bib} />
    <ol className="min-w-0 flex-1 space-y-0.5">
      {(p.members?.length ? p.members : [{ name: p.name }]).map((m, i) => (
        <li key={i} className="flex items-baseline gap-2 text-lg font-bold uppercase leading-tight">
          {p.members?.length > 2 && (
            <span className="shrink-0 w-5 text-sm font-semibold text-white/50 tabular-nums">{i + 1}</span>
          )}
          <span className="min-w-0 break-words">{m.name}</span>
        </li>
      ))}
    </ol>
  </li>
);

/**
 * One card, styled like a column on the display board: a single thin gold
 * border around a gold header and the rows. Cards used to nest (a gold bar,
 * then a box, then boxes inside it), which read nothing like the board.
 */
const Card = ({ title, count, children }) => (
  <section
    className="min-w-0 rounded-2xl overflow-hidden border"
    style={{ borderColor: "rgba(246,184,0,0.35)", background: "rgba(2,10,40,0.55)", backdropFilter: "blur(6px)" }}
  >
    <header
      className="flex items-baseline justify-between px-4 py-2"
      style={{ background: "rgba(246,184,0,0.92)", color: NAVY }}
    >
      <h3 className="font-black italic uppercase text-2xl leading-[1.1] pr-1">{title}</h3>
      <span className="font-bold tabular-nums">{count}</span>
    </header>
    <ul className="divide-y divide-white/10 p-1">{children}</ul>
  </section>
);

export const AquaFitXStartlist = () => {
  useAquaFitXFont();
  const { realEventId: eventId } = useEventId();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [selected, setSelected] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!eventId) return;
    // The event document straight from Firestore: the API may be asleep.
    getDoc(doc(getFirestore(), "event_timing", eventId))
      .then((snapshot) => setEvent(snapshot.exists() ? snapshot.data() : null))
      .catch(() => {});
    // From Firestore: the participants API leaves out team members.
    getDocs(collection(getFirestore(), "event_timing", eventId, "participants"))
      .then((snapshot) => setParticipants(snapshot.docs.map((d) => d.data())))
      .catch(() => {});
  }, [eventId]);

  const categories = event?.categories || [];
  const q = query.trim().toLowerCase();
  const matches = (p) =>
    !q ||
    String(p.bib).includes(q) ||
    String(p.name || "").toLowerCase().includes(q) ||
    (p.members || []).some((m) => m.name.toLowerCase().includes(q));

  const sections = useMemo(
    () =>
      categories
        .filter((c) => selected === "all" || c.id === selected)
        .map((c) => {
          const rows = participants
            .filter((p) => p.categoryId === c.id && matches(p))
            .sort((a, b) => Number(a.bib) - Number(b.bib));
          const isTeam = participants.some((p) => p.categoryId === c.id && p.members?.length);
          return { category: c, rows, isTeam };
        })
        .filter((s) => s.rows.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, participants, selected, q],
  );

  const chip = (active) =>
    active
      ? { background: GOLD, color: NAVY }
      : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.8)" };

  return (
    <div
      className="min-h-screen text-white"
      style={{
        fontFamily: AQUAFITX_FONT,
        backgroundColor: NAVY,
        backgroundImage: AQUAFITX_BG,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Full width with the display board's side margin, not a centred column. */}
      <div className="px-4 py-6 md:px-8 md:py-8">
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="font-black italic uppercase text-4xl md:text-5xl leading-[1.05] pr-2">
              AquaFit<span style={{ color: GOLD }}>X</span> Race
            </div>
            <div className="mt-1 text-sm md:text-base font-semibold text-white/70">
              START LIST
            </div>
            <div className="mt-2 text-sm md:text-base font-semibold text-white/70 uppercase">
              {[formatDate(event?.date), event?.location].filter(Boolean).join(" · ")}
              {participants.length ? ` · ${participants.length} entries` : ""}
            </div>
          </div>
        </header>

        <nav className="mt-6 flex flex-wrap gap-2">
          {[{ id: "all", name: "All" }, ...categories].map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelected(c.id)}
              className="rounded-full px-3.5 py-1.5 text-sm font-bold uppercase"
              style={chip(selected === c.id)}
            >
              {shortName(c.name)}
            </button>
          ))}
        </nav>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search BIB or name"
          className="mt-4 w-full md:w-96 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-base text-white placeholder-white/50 outline-none focus:border-white/50"
          style={{ fontFamily: "inherit" }}
        />

        <div className="mt-6 space-y-8">
          {sections.map(({ category, rows, isTeam }) => {
            const men = rows.filter((p) => p.gender === "male");
            const women = rows.filter((p) => p.gender === "female");
            return (
              <div key={category.id}>
                <div className="mb-3">
                  <div className="text-xs md:text-sm font-bold" style={{ color: GOLD }}>
                    CATEGORY
                  </div>
                  <h2 className="font-black italic uppercase text-3xl md:text-4xl leading-[1.1] pr-2">
                    {category.name}
                  </h2>
                </div>
                {isTeam ? (
                  <Card title="Teams" count={`${rows.length} TEAMS`}>
                    {rows.map((p) => <TeamRow key={p.bib} p={p} />)}
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 items-start">
                    {men.length > 0 && (
                      <Card title="Men" count={`${men.length} ATHLETES`}>
                        {men.map((p) => <IndividualRow key={p.bib} p={p} />)}
                      </Card>
                    )}
                    {women.length > 0 && (
                      <Card title="Women" count={`${women.length} ATHLETES`}>
                        {women.map((p) => <IndividualRow key={p.bib} p={p} />)}
                      </Card>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!sections.length && (
            <p className="py-16 text-center text-xl font-bold italic uppercase text-white/60">
              {participants.length ? "No match" : "Loading…"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AquaFitXStartlist;
